import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { RoleFormView } from './role-form.view';
import { PermissionDataService } from './permission.data';
import { RoleDataService, RoleItemModel } from './role-form.data';
import { IdentityService } from 'src/core/services/identity.service';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	defaultLanguage: string;
	mode: ControlMode;
	refresh: boolean;
}

@observer
export class RoleFormController extends React.Component<Props, State> {
	@observable
	protected dataService: RoleDataService;

	@resolve
	private identityService: IdentityService;

	@resolve
	private toast: ToastNotifications;

	@resolve
	protected configurationService: ConfigurationService;

	@observable
	private permissionDataService: PermissionDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			refresh: false,
			mode: 'display',
			defaultLanguage: 'en'
		};
	}

	@computed
	protected get title() {
		const isNew = this.props.match.params.id == 'new';
		if (isNew) {
			return 'New Role';
		} else {
			return `${this.dataService.item.displayName}`;
		}
	}

	private isAdministrator() {
		return this.identityService.isAdmin;
	}

	private backUrl() {
		return this.isAdministrator() ? '/general/roles' : '/';
	}

	handleModeChange = (mode: ControlMode) => {
		this.setState({ mode });
	}

	@autobind
	protected create() {
		const role = this.dataService.add();
		return role;
	}

	@autobind
	async save() {
		const item = this.dataService.item as RoleItemModel;
		const errors = await item.validate(true);

		if (errors.length == 0) {
			this.props.setBusy(true);

			this.dataService.save()
				.then(() => {
					this.handleModeChange('display');
					this.toast.showDataSaved();
					this.props.history.replace(this.backUrl());
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	cancel() {
		this.props.history.push(this.backUrl());
	}

	public componentDidMount(): void {
		this.dataService = RoleDataService.make();
		this.permissionDataService = PermissionDataService.make();

		if (!this.isAdministrator()) {
			this.props.history.push('/login', { from: this.props.location, requirePrivileges: true });
			return;
		}

		this.props.setBusy(true);
		this.loadPermissions();
		this.configurationService.current()
			.then((appConfig) => {
				this.setState({ defaultLanguage: appConfig.defaultLanguage });
				if (this.props.match.params.id == 'new') {
					//Create new
					this.dataService.create(this.create)
						.then(() => {
							this.handleModeChange('edit');
							this.props.setBusy(false);
						})
						.catch((error) => {
							this.props.setBusy(false);
							this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
						});
				} else {
					//Load entity
					this.dataService.addFilter('id', 'eq', this.props.match.params.id);
					this.dataService.addSorting('id', 'asc', true);
					this.dataService.pageSize = 1;
					this.dataService.load(query => query)
						.catch((error) => {
							this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
						})
						.then(() => {
							this.props.setBusy(false);
						});
				}
			});
	}

	public render(): React.ReactNode {
		if (this.dataService == null || this.dataService.item == null || this.permissionDataService == null) {
			return null;
		}

		return (
			<RoleFormView
				title={this.title}
				mode={this.state.mode}
				isAdministrator={this.isAdministrator()}
				operation={this.props.match.params.id == 'new' ? 'new' : 'update'}
				onModeChanged={this.handleModeChange}
				cancel={this.cancel}
				save={this.save}
				permissions={this.permissionDataService.items.slice()}
				model={this.dataService.item}
				setBusy={this.props.setBusy}
				contextService={this.dataService}
			/>
		);
	}

	private async loadPermissions(): Promise<void> {
		this.permissionDataService.addSorting("module", "asc");
		this.permissionDataService.addSorting("name", "asc");
		this.permissionDataService.pageSize = 1000;
		await this.permissionDataService.load(query => query);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/roles/form/role-form.controller.tsx